import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import List from "src/components/List";
import LovBusiness from "src/components/Lov/Business";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import LovCommercialPlans from "src/components/Lov/CommercialPlans";
import LovContractItems from "src/components/Lov/ContractItems";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import returnValueForSelect from "src/utils/returnValueForSelect";
import { IRefModalViewContract } from "../BusinessGroup/interface";
import ModalViewContract from "../BusinessGroup/modalViewContract";
import ListChild from "./components/listChild";
import { IListData } from "./interface";
import ColTitle from "./styles";


const ContractLimits: React.FC = () => {

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const refModalViewContract = useRef<IRefModalViewContract>(null);
	
	const [data, setData] = useState<IListData[]>([]);
	const [listData, setListData] = useState<IListData[]>([]);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<
	{ 
		group: string, 
		business: string, 
		situation: string,
		contractType: string,
		dtIniContract: Date | null,
    dtFinContract: Date | null,
		commercialPlan: string,
		sort: string
	}>({ 
		group: objQueryParams?.businessgroup || "",
		business: "", 
		situation: "T",
		contractType: "",
		commercialPlan: "",
		dtIniContract: null,
		dtFinContract: null,
		sort: "efe001Dtfim"
	});

	const { 
		handleSubmit : handleSubmitFilters,
		watch : watchFilters, 
		reset: resetFilters ,
		setValue: setValueFilters,
		control: controlFilters
	} = useForm();

	const submitFilters = (dataX: any) => {
		setFilters({
			situation: dataX.situation?.value || dataX.situation, 
			group: dataX.group?.value || dataX.group,
			contractType: dataX.contractType?.value || dataX.contractType,
			commercialPlan: dataX.commercialPlan?.value || dataX.commercialPlan,
			business:  dataX.business?.value || dataX.business ,
			dtFinContract: dataX.dtFinContract,
			dtIniContract: dataX.dtIniContract,
			sort: dataX.sort?.value || dataX.sort
		});
	};

	const requestData = async () => {
		setLoading(true);
		try{
			const response  = await RCCServicesApi.getRequest({
				method: "ContractLimits/List",
				urlParameters: {
					group: filters.group ?? "",
					business: filters.business ?? "",
					situation: filters.situation && filters.situation !== "T" ? filters.situation : "",
					dtIniContract: filters.dtIniContract ? filters.dtIniContract.toLocaleDateString() : "",
					dtFinContract: filters.dtFinContract ?  filters.dtFinContract.toLocaleDateString() : "",
					commercialPlan: filters.commercialPlan ?? "",
					contractType: filters.contractType ?? "",
					sort: filters.sort ?? ""
				}
			});
			setData(response.data);
			setLoading(false);
		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const colTitle = (row: any) => (
		<ColTitle>
			<span onClick={() => { 	refModalViewContract.current?.actionButtonViewContract(row.efa001Id, row.efa001Fantasia, requestData); }}
				aria-hidden="true">{row.efa001Fantasia}</span>
		</ColTitle>
	);

	const listChild = (dt: any) => (
		<>
			<ListChild 
				data={dt}
			/>
		</>
	);

	useEffect(() => {
		setListData(data.filter((e) => e.eftba012 && (e.eftba012?.filter((x) => x.efa012Valor > 0).length > 0)));
	},[data]);

	useEffect(() => {
		requestData();
	},[filters]);

	const watchFilterGroup =  watchFilters("group");

	const situationOptions = [{
		value: "3",
		label: translate("Ultrapassou")
	},{
		value: "T",
		label: translate("Todos")
	},{
		value: "1",
		label: translate("Liberado")
	},{
		value: "2",
		label: translate("Alerta")
	}];

	const sortOptions = [{
		value: "efa001Razao",
		label: translate("Razão Social")
	},{
		value: "efe001Dtfim",
		label: translate("Data de vencimento")
	}];

	const hasFilters = showFilters || filters.group || filters.business || filters.situation !== "T" || filters.commercialPlan || filters.contractType || filters.dtFinContract || filters.dtIniContract;	

	return (
		<>

			<PageTitle
				title={translate("Monitorar contratos")}
				icon={() => <AppIcons name="configs" />}
			>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={ hasFilters ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>

			<FiltersContainer
				onSubmitFilters={handleSubmitFilters(submitFilters)}
				resetFilters={() => resetFilters({ 
					group: "", 
					business: "", 
					situation: returnValueForSelect("T", situationOptions), 
					filter: "",
					commercialPlan: "",
					contractType: "",
					dtIniContract: null,
					dtFinContract: null,
					sort: returnValueForSelect("efe001Dtfim", sortOptions)
				})}
				showFilters={showFilters}
			>
				<RCCGrid
					container
					item
					spacing={2}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<LovBusinessGroups 
							control={controlFilters}
							name="group" 
							defaultValue={filters.group ?? ""}
							label={translate("Grupo de Empresas")}
							submit="value and label"
							onChangeCapture={() => {
								setValueFilters("business", undefined);
							}}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<LovBusiness 
						  key={watchFilterGroup?.value || watchFilterGroup}
							control={controlFilters}
							name="business"
							label={translate("Empresa")}
							defaultValue={filters.business ?? ""}
							submit="value and label"
							disabled={!watchFilterGroup}
							extraParametersOnLoad={{
			           group: watchFilterGroup?.value || watchFilterGroup
							}}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<LovContractItems
							control={controlFilters}
							name="contractType"
							label={translate("Item de contrato")}
							defaultValue={filters.contractType ?? ""}
							submit="value and label"
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							control={controlFilters}
							name="situation"
							label={translate("Situação")}
							defaultValue={returnValueForSelect(filters.situation, situationOptions)}
							submit="value and label"
							options={situationOptions}
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<LovCommercialPlans 
							name="commercialPlan"
							label={translate("Plano comercial")}
							control={controlFilters}
							submit="value and label"
							defaultValue={filters.commercialPlan ?? ""}
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<DateTimePickerRange
							control={controlFilters}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit = "dtIniContract"
							nameEnd = "dtFinContract"
							defaultValueEnd={filters.dtFinContract}
							defaultValueInit={filters.dtIniContract}
							type="datePicker"
							setValue={setValueFilters}
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							control={controlFilters}
							name="sort"
							label={translate("Ordenar")}
							defaultValue={returnValueForSelect(filters.sort, sortOptions)}
							submit="value and label"
							options={sortOptions}
						/>
					</RCCGrid>
				</RCCGrid>
			</FiltersContainer>
    

			<List 
				data={listData}
				layout="table"
				showHeader
				columns={[{
					idColumn: "efa001Razao",
					nameColumn: translate("Grupo empresarial"),
					xs: 12,
					sm: 6,
					justify: "left",
					cellRenderer: colTitle
				},{
					idColumn: "efe001Efd001Descricao",
					nameColumn: translate("Planos comerciais"),
					xs: 12,
					sm: 3,
					justify: "left",
					cellRenderer: (row: any) =>  `${row.eftbe001?.efe001Efd001Descricao ?? ""}`
				},{
					idColumn: "efe001Dtfim",
					nameColumn: translate("Vigência"),
					xs: 12,
					sm: 3,
					justify: "left",
					cellRenderer: (row: any) =>  `${formatDate(row.eftbe001?.efe001Dtinicio, currentLanguage)} ${translate("até")} ${formatDate(row.eftbe001?.efe001Dtfim, currentLanguage)}`
				}]}
				childrenOfLine={{
					cellRenderer: listChild,
					conditionToRender: (row: any) => row.eftba012 && row.eftba012.filter((x: any) => x.efa012Valor > 0).length > 0
				}}
			/>						

				
			<ModalViewContract 
				ref={refModalViewContract}
			/>

		</>
	);

};

export default ContractLimits;
